import { useState } from "react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { cn } from "@/utils";

interface Props
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
    React.RefAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string;
  type?: string;
  unit?: string;
  icon?: React.ReactNode;
  error?: string;
  placeholder?: string;
  labelBgClass?: string;
  containerClassName?: string;
  clearable?: boolean;
  onClearable?: () => void;
  rows?: number;
  classNameIcon?: string;
  multiline?: boolean;
  resize?: "none" | "both" | "horizontal" | "vertical";
  withAsterisk?: boolean;
  numbersOnly?: boolean;
  allowDecimal?: boolean;
  allowNegative?: boolean;
}

export function Input(props: Props) {
  const {
    disabled,
    type = "text",
    label = "",
    required,
    unit,
    icon,
    className,
    error,
    containerClassName,
    labelBgClass,
    maxLength = 255,
    placeholder = "入力してください。",
    classNameIcon,
    hidden,
    multiline = false,
    rows = 3,
    resize = "vertical",
    withAsterisk = false,
    numbersOnly = false,
    allowDecimal = false,
    allowNegative = false,
    onKeyDown,
    onInput,
    ...rest
  } = props;

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (numbersOnly) {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (allowedKeys.includes(e.key)) {
        onKeyDown?.(e);
        return;
      }

      if (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
        onKeyDown?.(e);
        return;
      }

      if (e.key >= "0" && e.key <= "9") {
        onKeyDown?.(e);
        return;
      }

      if (allowDecimal && (e.key === "." || e.key === ",")) {
        const target = e.target as HTMLInputElement;
        const value = target.value;

        if (value.includes(".") || value.includes(",")) {
          e.preventDefault();
          return;
        }

        onKeyDown?.(e);
        return;
      }

      if (allowNegative && e.key === "-") {
        const target = e.target as HTMLInputElement;
        const selectionStart = target.selectionStart || 0;

        if (selectionStart === 0 && !target.value.includes("-")) {
          onKeyDown?.(e);
          return;
        }
      }

      e.preventDefault();
    } else {
      onKeyDown?.(e);
    }
  };

  const handleInput = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (numbersOnly) {
      const target = e.target as HTMLInputElement;
      let value = target.value;

      let pattern = "[^0-9]";
      if (allowDecimal) {
        pattern = "[^0-9.,]";
      }
      if (allowNegative) {
        pattern = allowDecimal ? "[^0-9.,-]" : "[^0-9-]";
      }

      let cleanValue = value.replace(new RegExp(pattern, "g"), "");

      if (allowDecimal) {
        const parts = cleanValue.split(/[.,]/);
        if (parts.length > 2) {
          cleanValue = parts[0] + "." + parts.slice(1).join("");
        } else if (parts.length === 2) {
          cleanValue = parts[0] + "." + parts[1];
        }
      }

      if (allowNegative) {
        const minusCount = (cleanValue.match(/-/g) || []).length;
        if (minusCount > 1) {
          cleanValue = "-" + cleanValue.replace(/-/g, "");
        } else if (cleanValue.includes("-") && !cleanValue.startsWith("-")) {
          cleanValue = "-" + cleanValue.replace("-", "");
        }
      }

      if (target.value !== cleanValue) {
        target.value = cleanValue;
      }
    }

    onInput?.(e);
  };

  const baseInputClasses = cn(
    "px-[0.88rem] font-normal text-sm leading-6 w-full placeholder:text-gray-400/80 placeholder:text-sm border border-gray-300 rounded-md focus:outline-none",
    className,
    unit ? "!pr-10" : "",
    icon ? "!pl-10" : "",
    disabled ? "bg-grey-200 cursor-not-allowed hover:border-gray-300" : "",
    error
      ? "!border-error_main focus:!border-error_main focus:!ring-error_main"
      : "",
    multiline ? "-mb-1.5 py-[0.75rem]" : "h-11",
  );

  const textareaClasses = cn(
    baseInputClasses,
    `resize-${resize}`,
    "hover:border hover:border-black focus:border focus:border-black",
  );

  // get title for input when disabled to show full content on hover
  const title = rest.title ?? (disabled ? rest.value?.toString() : undefined);

  return (
    <div className={cn("relative", containerClassName)} hidden={hidden}>
      {label && (
        <label
          className={cn(
            "absolute left-4 top-0 -translate-y-1/2 px-1 text-xs leading-3 z-[2]",
            error ? "text-error_main" : "text-grey-600",
            labelBgClass || "",
          )}
          style={{
            background: disabled
              ? (labelBgClass ??
                "linear-gradient(to bottom, transparent 50%, #F4F6F8 50%)")
              : (labelBgClass ?? "#ffffff"),
          }}
        >
          {label}
          {(required || withAsterisk) && (
            <span className="text-error_main ml-1">*</span>
          )}
        </label>
      )}

      {multiline ? (
        <textarea
          placeholder={placeholder}
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          rows={rows}
          className={textareaClasses}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          title={title}
        />
      ) : (
        <input
          type={!showPassword && type === "password" ? "password" : "text"}
          placeholder={placeholder}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          disabled={disabled}
          // TODO remove required và dùng register để set required trong form khi cần (dùng required mặc định sẽ hiển thị thông báo mặc định vd: Please fillout this field  nên cần remove)
          // Thêm lại required, để giải quyết thông báo Please fill out this field chỉ cần thêm prop noValidate vào form là được.
          required={required}
          maxLength={maxLength}
          className={baseInputClasses}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          title={title}
        />
      )}

      {icon && !multiline && (
        <span
          className={cn(
            "absolute top-1/2 -translate-y-1/2 left-[0.88rem]",
            !!error
              ? "top-[30%] transform -translate-y-[30%]"
              : "top-1/2 transform -translate-y-1/2",
            classNameIcon,
          )}
        >
          {icon}
        </span>
      )}

      {icon && multiline && (
        <span
          className={cn(
            "absolute top-4 left-[0.88rem]",
            !!error
              ? "top-[30%] transform -translate-y-[30%]"
              : "top-1/2 transform -translate-y-1/2",
            classNameIcon,
          )}
        >
          {icon}
        </span>
      )}

      {unit && !multiline && (
        <span
          className={cn(
            "absolute top-1/2 -translate-y-1/2 right-[0.88rem] text-xs leading-3 font-normal",
            !!error
              ? "top-[30%] transform -translate-y-[30%]"
              : "top-1/2 transform -translate-y-1/2",
          )}
        >
          {unit}
        </span>
      )}

      {unit && multiline && (
        <span
          className={cn(
            "absolute top-4 right-[0.88rem] text-xs leading-3 font-normal",
            !!error
              ? "top-[30%] transform -translate-y-[30%]"
              : "top-1/2 transform -translate-y-1/2",
          )}
        >
          {unit}
        </span>
      )}

      {type === "password" && !multiline && (
        <button
          type="button"
          className={cn(
            "absolute right-3 bg-transparent border-none text-gray-500 cursor-pointer p-0 text-base hover:text-gray-700 transition-colors duration-200",
            !!error
              ? "top-[30%] transform -translate-y-[30%]"
              : "top-1/2 transform -translate-y-1/2",
          )}
          onClick={togglePasswordVisibility}
          disabled={disabled}
          aria-label={!showPassword ? "Hide password" : "Show password"}
        >
          {!showPassword ? <HiEyeOff /> : <HiEye />}
        </button>
      )}

      {error && <p className="text-error_main text-xs mt-1">{error}</p>}
    </div>
  );
}
